import type { WallOpening } from "../framing/generateFramedWall";
import { CC_M, STUD_D_M, STUD_W_M } from "../geometry/buildingDimensions";
import type { CutRow } from "../../engines/types";

/** Coordinates in metres from the building centre (x) and wall base (y). */
export interface PartitionMember {
	id: string;
	x: number;
	y: number;
	width: number;
	height: number;
	slope: number;
	lengthMm: number;
	role: "stud" | "plate" | "header";
}

export function createPartitionFramePlan(
	width: number,
	door: WallOpening,
	topAt: (x: number) => number,
	gable: boolean,
) {
	const min = -width / 2 + STUD_D_M;
	const max = width / 2 - STUD_D_M;
	const left = -width / 2 + door.xMm / 1000;
	const right = left + door.widthMm / 1000;
	const doorTop = (door.yMm + door.heightMm) / 1000;
	const sw = STUD_W_M;
	const members: PartitionMember[] = [];
	const add = (
		id: string,
		x: number,
		y: number,
		w: number,
		h: number,
		role: PartitionMember["role"],
		slope = 0,
	) => {
		if (w <= 0 || h <= 0) return;
		members.push({
			id,
			x,
			y,
			width: w,
			height: h,
			role,
			slope,
			lengthMm: Math.round((role === "stud" ? h : w / Math.cos(slope)) * 1000),
		});
	};
	// Full-height jambs carry the partition's door fixings. This is a non-bearing wall.
	const jambs = [left - sw / 2, right + sw / 2];
	for (const [i, x] of jambs.entries()) {
		const top = Math.min(topAt(x - sw / 2), topAt(x + sw / 2)) - sw;
		add(`jamb-${i + 1}`, x, (top + sw) / 2, sw, top - sw, "stud");
	}
	const count = Math.ceil((max - min - sw) / CC_M) + 1;
	for (let i = 0; i < count; i++) {
		const x = min + sw / 2 + (i * (max - min - sw)) / (count - 1);
		if (jambs.some((j) => Math.abs(x - j) < sw - 1e-6)) continue;
		const inside = x + sw / 2 > left && x - sw / 2 < right;
		if (inside && (x - sw / 2 < left || x + sw / 2 > right)) continue;
		const bottom = inside ? doorTop + sw : sw;
		const top = Math.min(topAt(x - sw / 2), topAt(x + sw / 2)) - sw;
		add(`stud-${i + 1}`, x, (top + bottom) / 2, sw, top - bottom, "stud");
	}
	add(
		"header",
		(left + right) / 2,
		doorTop + sw / 2,
		right - left,
		sw,
		"header",
	);
	for (const [i, [a, b]] of [
		[min, left],
		[right, max],
	].entries())
		add(
			i === 0 ? "sill" : "sill-right",
			(a + b) / 2,
			sw / 2,
			b - a,
			sw,
			"plate",
		);
	for (const [i, [a, b]] of (gable
		? [
				[min, 0],
				[0, max],
			]
		: [[min, max]]
	).entries()) {
		const slope = Math.atan2(topAt(b) - topAt(a), b - a);
		add(
			gable ? (i === 0 ? "top-plate-left" : "top-plate-right") : "top-plate",
			(a + b) / 2,
			(topAt(a) + topAt(b)) / 2 - sw / 2,
			b - a,
			sw,
			"plate",
			slope,
		);
	}
	const cuts: CutRow[] = members.map((m) => ({
		part: `Delningsvägg ${m.id}`,
		count: "1 st",
		cutLength: `${m.lengthMm} mm`,
		purchaseDimension: "45x95",
		materialKey: m.role === "stud" ? "partition.studs" : "partition.plates",
		pieces: [{ lengthMm: m.lengthMm, count: 1 }],
	}));
	return {
		members,
		cuts,
		totalLm: members.reduce((sum, m) => sum + m.lengthMm / 1000, 0),
	};
}
